/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import CaseInsensitiveMap from './CaseInsensitiveMap';
import QuerySubjectUtils from './QuerySubjectUtils';
import IdentifierUtil from './IdentifierUtil';
import MoserObjectUtils from './MoserObjectUtils';
import UseSpecType from './UseSpecType';
import ItemScanner from './ItemScanner';
//@import BaseMoserObject (loadtime) // superclass
//@import CaseInsensitiveMap (runtime) // new
//@import com/ibm/bi/platform/moser/core/module/util/QuerySubjectUtils (static) // getParentModule, getParentQuerySubject
//@import com/ibm/bi/platform/moser/common/utils/IdentifierUtil (static) // isIdValid, makeUniqueAndValidIdentifier
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (runtime) // getGlobalCalculations, getItems, getItemNormalizationGroups, getFolderTypes
//@import com/ibm/bi/platform/moser/common/generated/metadata/UseSpecType (runtime) // UseSpecType
//@import com/ibm/bi/platform/moser/common/utils/ItemScanner (runtime) // collectIdentifiers

/**
 * Id Generator
 */
export default class IdGenerator extends BaseMoserObject{

	static ID_SEPARATOR = 46;

	static FM_ID_OPEN = 91;

	static FM_ID_CLOSE = 93;

	_ids = null;

	_scopeId = null;



	/**
	 * Initialize the generator with a scope
	 * @param {ObjectType} scope
	 */
	constructor(scope) {
		super();
		this._ids = new CaseInsensitiveMap();
		if (scope) {
			var actualScope = IdGenerator._adjustScope(scope);
			if ("QueryItem" == scope.getObjectType()) {
				this._scopeId = scope.getIdForExpression();
			} else if ("QuerySubject" == actualScope.getObjectType() || "WhatIfModel" == actualScope.getObjectType() || "DrillGroup" == actualScope.getObjectType()) {
				this._scopeId = actualScope.getIdentifier();
			}
			if ("Calculation" == actualScope.getObjectType()) {
				this._scopeId = actualScope.getIdentifier();
				var module = QuerySubjectUtils.getParentModule(actualScope);
				this._collectScopeIds(module);
			} else {
				this._collectScopeIds(actualScope);
			}
		}
	}

	/**
	 * Adjust a scope if required.
	 * @param {ObjectType} scope
	 * @return {ObjectType}
	 */
	static _adjustScope(scope) {
		var objectType = scope.getObjectType();
		if ("QueryItem" == objectType || "Folder" == objectType) {
			return QuerySubjectUtils.getParentQuerySubject(scope);
		} else if ("FolderType" == objectType || "Translation" == objectType) {
			return QuerySubjectUtils.getParentModule(scope);
		}
		return scope;
	}

	/**
	 * Generate and id for a given object within the scope
	 * @param {ObjectType} obj
	 */
	generateId(obj) {
		var prefix = obj.getIdentifier();
		if (prefix == null || prefix.length === 0) {
			prefix = obj.getLabel();
			if (prefix == null || prefix.length === 0) {
				prefix = "_id";
			} else if ("Module" == obj.getObjectType() && (obj).getContainer() != null) {
				prefix = (obj).getContainer() + "." + prefix;
			}
		}
		var id = IdentifierUtil.makeUniqueAndValidIdentifier(prefix, this._ids);
		obj.setIdentifier(id);
		if (!("Module" == obj.getObjectType() || "Folder" == obj.getObjectType() || "FolderType" == obj.getObjectType() || "Segment" == obj.getObjectType())) {
			if (this._scopeId == null || this._scopeId.length === 0) {
				obj.setIdForExpression(id);
			} else {
				obj.setIdForExpression(this._scopeId + "." + id);
			}
		}
		this._ids.put(id, Boolean["TRUE"]);
	}

	/**
	 * Generate a new usespec id
	 * @param {Module} module
	 * @return {String}  the new usespec id
	 */
	static generateNewUseSpecId(module) {
		return IdGenerator.generateNewUseSpecIdWithPrefix(module, "M");
	}

	/**
	 * Generate a new usespec id using a prefix (like M or __Base)
	 * @param {Module} module
	 * @param {String} prefix
	 * @return {String}  the new usespec id
	 */
	static generateNewUseSpecIdWithPrefix(module, prefix) {
		var useSpecList = module.basicGetUseSpec();
		var nextId = 0;
		if (useSpecList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = useSpecList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var spec = __exp_enFor0[__i_enFor0];
				if (spec.getIdentifier().indexOf(prefix) == 0) {
					var id = parseInt(spec.getIdentifier().substring(prefix.length));
					if (id > nextId) {
						nextId = id;
					}
				}
			}
		}
		nextId++;
		return prefix + nextId;
	}

	_collectScopeIds(scope) {
		if ("Module" == scope.getObjectType()) {
			this._collectIds((scope).basicGetQuerySubject());
			this._collectIds(MoserObjectUtils.getGlobalCalculations(scope));
			this._collectIds((scope).basicGetDrillGroup());
			this._collectIds((scope).basicGetFilter());
			this._collectIds((scope).basicGetParameterMap());
			this._collectIds((scope).basicGetRelationship());
			this._collectIds((scope).basicGetNamedSet());
			this._collectIds((scope).basicGetWhatIfModel());
			this._collectIds((scope).basicGetTranslation());
			this._collectIds((scope).basicGetCustomSort());
			this._collectIds((scope).basicGetUseSpec());
			this._collectIds(MoserObjectUtils.getFolderTypes(scope));
		} else if ("QuerySubject" == scope.getObjectType()) {
			this._collectIds(MoserObjectUtils.getItems(scope));
			this._collectIds((scope).basicGetFilter());
			this._collectIds((scope).basicGetSecurityFilter());
			this._collectIds(MoserObjectUtils.getItemNormalizationGroups(scope));
		} else if ("QueryItem" == scope.getObjectType()) {
			this._collectIds(MoserObjectUtils.getItems(scope));
		} else if ("WhatIfModel" == scope.getObjectType()) {
			this._collectIds((scope).basicGetCalculation());
		} else if ("DrillGroup" == scope.getObjectType()) {
			this._collectIds((scope).basicGetSegment());
		}
	}

	isIdUniqueInScope(idToCheck) {
		return !(this._ids.containsKey(idToCheck));
	}

	_collectIds(itemList) {
		if (itemList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = itemList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var obj = __exp_enFor0[__i_enFor0];
				var id = null;
				if (!("UseSpec" == obj.getObjectType())) {
					id = (obj).getIdentifier();
				} else if (UseSpecType.PACKAGE === (obj).getType()) {
					id = (obj).getIdentifier();
				}
				if (id != null && !(id.length === 0)) {
					this._ids.put(id, Boolean["TRUE"]);
				}
			}
		}
	}

	static idIsAllowedToBeChanged(obj) {
		if (!obj) {
			return true;
		}
		if ("QueryItem" == obj.getObjectType()) {
			var expr = (obj).getExpression();
			var id = obj.getIdentifier();
			if (id != null && !(id.length === 0) && expr != null && !(expr.length === 0)) {
				var ids = ItemScanner.collectIdentifiers(expr);
				for (var __i_enFor0 = 0, __exp_enFor0 = ids, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var parts = __exp_enFor0[__i_enFor0];
					if (parts.length == 1 && id == parts[0]) {
						return false;
					}
				}
			}
		}
		return true;
	}

	static isIdUnique(obj, idToCheck) {
		if (!obj) {
			return true;
		}
		var scopeObj;
		var parent = obj.getParent();
		while (parent) {
			var objType = parent.getObjectType();
			if ("Module" == objType || "QuerySubject" == objType) {
				scopeObj = parent;
				break;
			}
			parent = parent.getParent();
		}
		if (!scopeObj) {
			return true;
		}
		var gen = new IdGenerator(null);
		gen._collectScopeIds(scopeObj);
		return !(gen._ids.containsKey(idToCheck));
	}

	static isIdValid(idToCheck) {
		return IdentifierUtil.isIdValid(idToCheck);
	}

	static makeIdentifier(parentObj, id) {
		if (!parentObj) {
			return null;
		}
		var scopeObj;
		var parent = parentObj;
		while (parent) {
			var objType = parent.getObjectType();
			if ("Module" == objType || "QuerySubject" == objType || "DrillGroup" == objType) {
				scopeObj = parent;
				break;
			}
			parent = parent.getParent();
		}
		if (!scopeObj) {
			return null;
		}
		var gen = new IdGenerator(null);
		gen._collectScopeIds(scopeObj);
		return IdentifierUtil.makeUniqueAndValidIdentifier(id, gen._ids);
	}

	/**
	 * Splits idForExpression, fm or not FM into parts
	 * @param {String} idForExpression
	 * @return {Array}  array of individual parts of the multipart name
	 */
	static splitQualifiedPath(idForExpression) {
		var parts = [];
		var part = "";
		var fm = false;
		for (var i = 0; i < idForExpression.length; ++i) {
			var c = idForExpression.charCodeAt(i);
			switch (c) {
			case 46:
				if (fm) {
					part += String.fromCharCode(c);
				} else {
					parts.push(part.valueOf());
					part = "";
				}
				break;
			case 91:
				fm = true;
				part += String.fromCharCode(c);
				break;
			case 93:
				fm = false;
			default:
				part += String.fromCharCode(c);
				if (i == idForExpression.length - 1) {
					parts.push(part.valueOf());
				}
				break;
			}
		}
		return parts;
	}
}


// WEBPACK FOOTER //
// ./src/generated/IdGenerator.js